import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import CourtApi, { FullCourtDto } from '../../api/CourtApi';
import { courtContactLines, courtNoEmailWarning, courtOptionLabel, isBlankCourt } from '../../utils/courtContact';
import './CourtSelect.scss';

interface CourtSelectProps {
  value?: FullCourtDto | null;
  onChange: (court: FullCourtDto | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const CourtSelect: React.FC<CourtSelectProps> = ({
  value,
  onChange,
  label = "Select Court",
  placeholder = "Search for courts...",
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FullCourtDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      setLoading(true);
      try {
        const searchParams: any = {
          size: 50, // Limit results
          Court_Active: true, // Only show active courts
        };

        if (searchTerm) {
          searchParams.search = searchTerm;
        }

        const response = await CourtApi.searchCourts(searchParams);
        setOptions(response.courts);
      } catch (error) {
        console.error('Error searching courts:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Effect to trigger search when input changes
  useEffect(() => {
    if (open) {
      debouncedSearch(inputValue);
    }
  }, [inputValue, debouncedSearch, open]);

  // Load initial options when component opens
  useEffect(() => {
    if (open && options.length === 0 && !inputValue) {
      debouncedSearch('');
    }
  }, [open, debouncedSearch, options.length, inputValue]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: FullCourtDto | null) => {
    onChange(newValue);
  };

  const formatOptionLabel = (option: FullCourtDto) => courtOptionLabel(option);

  // A court with no email can be chosen, but says so: a letter cannot be emailed to it.
  const noEmailWarning = helperText ? null : courtNoEmailWarning(value);

  const renderOption = (props: any, option: FullCourtDto) => (
    <Box component="li" {...props} key={option.Court_guid}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" component="div">
            {option.Court_Name}
          </Typography>
          {isBlankCourt(option) && (
            <Chip label="Blank record" size="small" color="warning" variant="outlined" />
          )}
        </Box>
        {courtContactLines(option).map((line) => (
          <Typography
            key={line.text}
            variant="body2"
            color={line.missing ? 'warning.main' : 'text.secondary'}
            component="div"
          >
            {line.text}
          </Typography>
        ))}
        {option.prosecutors && option.prosecutors.length > 0 && (
          <Chip
            label={`${option.prosecutors.length} Prosecutor${option.prosecutors.length > 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <div className="court-select">
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={formatOptionLabel}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => 
          option.Court_guid === value?.Court_guid
        }
        filterOptions={(options) => options} // Disable built-in filtering since we're doing server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText ?? noEmailWarning ?? undefined}
            FormHelperTextProps={noEmailWarning && !error ? { sx: { color: 'warning.main' } } : undefined}
            required={required}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.Court_guid}
              label={option.Court_Name}
              size="small"
            />
          ))
        }
        noOptionsText={
          inputValue ? 
            "No courts found" : 
            "Start typing to search for courts"
        }
        loadingText="Searching courts..."
      />
    </div>
  );
};
export default CourtSelect;
